import { first } from 'lodash';

import {
  makeCreatePolicyInputs,
  makeUpdatePolicyInputs,
} from '@/modules/entities/EntitySubforms/utils/shared/policies.utils';
import {
  AugmentedCreateInsurancePolicyInput,
  AugmentedUpdateInsurancePolicyInput,
} from '@/types/schema';

import { HypotheticalILITInsurancePolicyType } from '../HypotheticalILITModal.types';
import { MapHypotheticalInsuranceToDataInput } from '../HypotheticalILITModal.utils';

export interface GetCreateInsurancePolicyOutput {
  updateCreatedInsurancePolicy: AugmentedUpdateInsurancePolicyInput | null;
  withCreatedInsurancePolicy: AugmentedCreateInsurancePolicyInput | null;
  clearCreatedInsurancePolicy?: boolean;
  errors?: string[];
}

export function getCreatedInsurancePolicyInputs({
  formData,
  contextData,
}: MapHypotheticalInsuranceToDataInput): GetCreateInsurancePolicyOutput {
  if (
    formData._hypotheticalInsurancePolicyType !==
    HypotheticalILITInsurancePolicyType.New
  ) {
    return {
      withCreatedInsurancePolicy: null,
      updateCreatedInsurancePolicy: null,
      clearCreatedInsurancePolicy: true,
      errors: [],
    };
  }

  const errors: string[] = [];

  // This is a bit backwards: the "created" in the field name
  // refers to the fact that the policy was created for the hILIT;
  // but on edit, it can already exist.  If it exists, do not
  // create it; instead, edit it.
  if (contextData.initialHypotheticalInsurance?.createdInsurancePolicy) {
    const updatePolicyInputs = makeUpdatePolicyInputs(
      formData.insurancePolicyDetailsSubform,
      contextData.householdId
    );
    const updateCreatedInsurancePolicy: AugmentedUpdateInsurancePolicyInput | null =
      first(updatePolicyInputs.updatePolicies) ?? null;

    if (!updateCreatedInsurancePolicy) {
      errors.push('Could not update created insurance policy');
    }
    return {
      withCreatedInsurancePolicy: null,
      updateCreatedInsurancePolicy,
      errors,
    };
  }

  const createPolicyInputs = makeCreatePolicyInputs(
    formData.insurancePolicyDetailsSubform,
    contextData.householdId
  );

  const withCreatedInsurancePolicy: GetCreateInsurancePolicyOutput['withCreatedInsurancePolicy'] =
    first(createPolicyInputs?.withPolicies) ?? null;

  if (!withCreatedInsurancePolicy) {
    errors.push('Could not create created insurance policy');
  }

  return {
    errors,
    withCreatedInsurancePolicy,
    updateCreatedInsurancePolicy: null,
  };
}
